import React, { useState, useEffect } from 'react';
import { useAuth, useAuthPrompt } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  RefreshCw,
  Plus,
  Trash2,
  Pause,
  Play,
  Bell,
  Calendar,
  DollarSign,
  Loader2,
  X,
  CreditCard,
  Tv,
  Wifi,
  Home,
  Car,
  Dumbbell,
  Music,
  Cloud,
  Smartphone,
  Shield,
  Zap,
  MoreHorizontal,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Subscription category icons and colors
const categoryConfig = {
  'Streaming': { icon: Tv, color: 'hsl(0 70% 55%)' },
  'Internet': { icon: Wifi, color: 'hsl(200 80% 55%)' },
  'Housing': { icon: Home, color: 'hsl(38 90% 55%)' },
  'Transportation': { icon: Car, color: 'hsl(210 50% 55%)' },
  'Fitness': { icon: Dumbbell, color: 'hsl(160 60% 50%)' },
  'Music': { icon: Music, color: 'hsl(280 60% 55%)' },
  'Cloud Storage': { icon: Cloud, color: 'hsl(200 60% 55%)' },
  'Phone': { icon: Smartphone, color: 'hsl(160 80% 45%)' },
  'Insurance': { icon: Shield, color: 'hsl(220 60% 55%)' },
  'Utilities': { icon: Zap, color: 'hsl(45 90% 50%)' },
  'Software': { icon: Cloud, color: 'hsl(250 60% 55%)' },
  'Other': { icon: CreditCard, color: 'hsl(0 0% 50%)' }
};

const frequencies = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' }
];

export default function Subscriptions() {
  const { isAuthenticated } = useAuth();
  const { showAuthPrompt } = useAuthPrompt();
  
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSub, setNewSub] = useState({
    name: '',
    amount: '',
    category: 'Other',
    frequency: 'monthly',
    next_due: '',
    reminder_days: 3,
    notes: ''
  });

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API_URL}/api/subscriptions`, { headers });
      setSubscriptions(res.data.subscriptions || []);
    } catch (error) {
      // Demo data
      setSubscriptions([
        { id: '1', name: 'Netflix', amount: 15.99, category: 'Streaming', frequency: 'monthly', next_due: '2025-03-01', status: 'active', reminder_days: 3 },
        { id: '2', name: 'Spotify', amount: 9.99, category: 'Music', frequency: 'monthly', next_due: '2025-03-05', status: 'active', reminder_days: 3 },
        { id: '3', name: 'Gym Membership', amount: 49.99, category: 'Fitness', frequency: 'monthly', next_due: '2025-03-01', status: 'active', reminder_days: 3 },
        { id: '4', name: 'iCloud Storage', amount: 2.99, category: 'Cloud Storage', frequency: 'monthly', next_due: '2025-03-10', status: 'active', reminder_days: 3 },
        { id: '5', name: 'Car Insurance', amount: 150.00, category: 'Insurance', frequency: 'monthly', next_due: '2025-03-15', status: 'active', reminder_days: 7 },
        { id: '6', name: 'Adobe Creative Cloud', amount: 54.99, category: 'Software', frequency: 'monthly', next_due: '2025-03-20', status: 'paused', reminder_days: 3 }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubscription = async () => {
    if (!isAuthenticated) {
      showAuthPrompt('Add Subscription');
      return;
    }
    if (!newSub.name || !newSub.amount || !newSub.next_due) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/subscriptions`, {
        ...newSub,
        amount: parseFloat(newSub.amount),
        reminder_days: parseInt(newSub.reminder_days)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Subscription added!');
      setShowAddModal(false);
      setNewSub({ name: '', amount: '', category: 'Other', frequency: 'monthly', next_due: '', reminder_days: 3, notes: '' });
      fetchSubscriptions();
    } catch (error) {
      toast.error('Failed to add subscription');
    }
  };

  const handleToggleStatus = async (sub) => {
    if (!isAuthenticated) {
      showAuthPrompt('Update Subscription');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const newStatus = sub.status === 'active' ? 'paused' : 'active';
      await axios.patch(`${API_URL}/api/subscriptions/${sub.id}`, {
        status: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(newStatus === 'active' ? 'Subscription resumed' : 'Subscription paused');
      fetchSubscriptions();
    } catch (error) {
      // Update locally for demo
      setSubscriptions(subscriptions.map(s => 
        s.id === sub.id ? { ...s, status: s.status === 'active' ? 'paused' : 'active' } : s
      ));
      toast.success(sub.status === 'active' ? 'Subscription paused' : 'Subscription resumed');
    }
  };

  const handleDelete = async (subId) => {
    if (!isAuthenticated) {
      showAuthPrompt('Delete Subscription');
      return;
    }
    if (!window.confirm('Delete this subscription?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/subscriptions/${subId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Subscription deleted');
      fetchSubscriptions();
    } catch (error) {
      // Remove locally for demo
      setSubscriptions(subscriptions.filter(s => s.id !== subId));
      toast.success('Subscription deleted');
    }
  };

  // Calculate totals
  const activeSubs = subscriptions.filter(s => s.status === 'active');
  const monthlyTotal = activeSubs.reduce((sum, s) => {
    let monthly = s.amount;
    if (s.frequency === 'weekly') monthly = s.amount * 4.33;
    else if (s.frequency === 'quarterly') monthly = s.amount / 3;
    else if (s.frequency === 'yearly') monthly = s.amount / 12;
    return sum + monthly;
  }, 0);
  const yearlyTotal = monthlyTotal * 12;

  // Find upcoming due
  const today = new Date();
  const upcomingDue = activeSubs
    .map(s => ({ ...s, daysUntil: Math.ceil((new Date(s.next_due) - today) / (1000 * 60 * 60 * 24)) }))
    .filter(s => s.daysUntil >= 0 && s.daysUntil <= 7)
    .sort((a, b) => a.daysUntil - b.daysUntil);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--accent))]" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="subscriptions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Space_Grotesk']">
            <span className="text-[hsl(var(--accent))]">Subscriptions</span>
          </h1>
          <p className="text-muted-foreground mt-1">Track recurring bills and subscriptions</p>
        </div>
        <Button onClick={() => setShowAddModal(true)} className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90">
          <Plus className="w-4 h-4 mr-2" /> Add Subscription
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="card-premium">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Monthly Total</p>
                <p className="text-2xl font-bold text-[hsl(var(--accent))]">{formatCurrency(monthlyTotal)}</p>
              </div>
              <div className="p-3 rounded-xl bg-[hsl(var(--accent))]/10">
                <RefreshCw className="w-6 h-6 text-[hsl(var(--accent))]" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-premium">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Yearly Total</p>
                <p className="text-2xl font-bold">{formatCurrency(yearlyTotal)}</p>
              </div>
              <div className="p-3 rounded-xl bg-purple-500/10">
                <Calendar className="w-6 h-6 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-premium">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold text-emerald-500">{activeSubs.length}</p>
              </div>
              <div className="p-3 rounded-xl bg-emerald-500/10">
                <CheckCircle className="w-6 h-6 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-premium">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Due This Week</p>
                <p className="text-2xl font-bold text-amber-500">{upcomingDue.length}</p>
              </div>
              <div className="p-3 rounded-xl bg-amber-500/10">
                <Bell className="w-6 h-6 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Due Alert */}
      {upcomingDue.length > 0 && (
        <Card className="card-premium border-amber-500/30 bg-amber-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-amber-500">
              <AlertCircle className="w-5 h-5" />
              Upcoming Payments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {upcomingDue.map(sub => (
                <div key={sub.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-background border border-border">
                  <span className="font-medium">{sub.name}</span>
                  <Badge variant="outline" className="text-amber-500 border-amber-500/30">
                    {sub.daysUntil === 0 ? 'Today' : sub.daysUntil === 1 ? 'Tomorrow' : `${sub.daysUntil} days`}
                  </Badge>
                  <span className="text-muted-foreground">{formatCurrency(sub.amount)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Subscriptions List */}
      <div className="space-y-3">
        {subscriptions.length > 0 ? (
          subscriptions.map((sub) => {
            const config = categoryConfig[sub.category] || categoryConfig['Other'];
            const Icon = config.icon;
            const isPaused = sub.status === 'paused';
            const daysUntil = Math.ceil((new Date(sub.next_due) - today) / (1000 * 60 * 60 * 24));
            
            return (
              <Card key={sub.id} className={`card-premium ${isPaused ? 'opacity-60' : ''}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-xl" style={{ backgroundColor: `${config.color}15` }}>
                        <Icon className="w-6 h-6" style={{ color: config.color }} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{sub.name}</h3>
                          {isPaused && (
                            <Badge variant="outline" className="text-xs">Paused</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <span>{sub.category}</span>
                          <span>•</span>
                          <span className="capitalize">{sub.frequency}</span>
                          <span>•</span>
                          <span className={daysUntil <= 3 && !isPaused ? 'text-amber-500 font-medium' : ''}>
                            Due {formatDate(sub.next_due)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-xl font-bold" style={{ color: isPaused ? 'inherit' : config.color }}>
                          {formatCurrency(sub.amount)}
                        </p>
                        <p className="text-xs text-muted-foreground">per {sub.frequency.replace('ly', '')}</p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleStatus(sub)}
                          className={isPaused ? 'text-emerald-500 hover:text-emerald-600' : 'text-amber-500 hover:text-amber-600'}
                        >
                          {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(sub.id)}
                          className="text-muted-foreground hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Card className="card-premium">
            <CardContent className="py-12 text-center text-muted-foreground">
              <RefreshCw className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No subscriptions yet</p>
              <p className="text-sm mt-1">Add your recurring bills and subscriptions to track them</p>
              <Button onClick={() => setShowAddModal(true)} className="mt-4">
                <Plus className="w-4 h-4 mr-2" /> Add Subscription
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add Subscription Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Add Subscription</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowAddModal(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Name *</Label>
                <Input
                  placeholder="e.g., Netflix, Spotify"
                  value={newSub.name}
                  onChange={(e) => setNewSub({ ...newSub, name: e.target.value })}
                  className="mt-1"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Amount *</Label>
                  <div className="relative mt-1">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="9.99"
                      value={newSub.amount}
                      onChange={(e) => setNewSub({ ...newSub, amount: e.target.value })}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label>Frequency</Label>
                  <select
                    value={newSub.frequency}
                    onChange={(e) => setNewSub({ ...newSub, frequency: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm mt-1"
                  >
                    {frequencies.map(f => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <Label>Category</Label>
                <select
                  value={newSub.category}
                  onChange={(e) => setNewSub({ ...newSub, category: e.target.value })}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm mt-1"
                >
                  {Object.keys(categoryConfig).map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label>Next Due Date *</Label>
                <Input
                  type="date"
                  value={newSub.next_due}
                  onChange={(e) => setNewSub({ ...newSub, next_due: e.target.value })}
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Remind me (days before)</Label>
                <select
                  value={newSub.reminder_days}
                  onChange={(e) => setNewSub({ ...newSub, reminder_days: e.target.value })}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm mt-1"
                >
                  <option value="1">1 day</option>
                  <option value="3">3 days</option>
                  <option value="5">5 days</option>
                  <option value="7">1 week</option>
                </select>
              </div>
              
              <Button onClick={handleCreateSubscription} className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90">
                Add Subscription
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
